import React from 'react';
import { NumericField } from './NumberInput.jsx';
import { useAppConfirm } from './AppDialog.jsx';
import { fmtMoney } from '../engine.js';

const BADGE_BASE = 'inline-flex items-center text-[0.62rem] font-bold px-[8px] py-[2px] rounded-full uppercase tracking-[0.08em] align-middle border';
const BADGE_ZB = `${BADGE_BASE} bg-[rgba(240,180,41,0.14)] text-accent-gold border-[rgba(240,180,41,0.35)]`;
const BADGE_AMT = `${BADGE_BASE} bg-[rgba(96,165,250,0.12)] text-accent-blue border-[rgba(96,165,250,0.35)] tabular-nums normal-case font-mono`;
const BADGE_OVR = `${BADGE_BASE} bg-[rgba(192,132,252,0.14)] text-accent-purple border-[rgba(192,132,252,0.40)]`;

const BTN_GHOST_SM = 'bg-transparent text-accent-blue border border-dashed border-border-strong px-[0.7rem] py-[0.3rem] rounded-md font-[inherit] text-[0.78rem] font-semibold cursor-pointer hover:border-accent-blue hover:bg-[rgba(96,165,250,0.08)] hover:text-[#93c5fd] transition-colors';
const BTN_RESET = 'bg-transparent text-text-muted border border-border-strong px-[0.6rem] py-[0.25rem] rounded-md font-[inherit] text-[0.72rem] cursor-pointer hover:text-accent-gold hover:border-accent-gold transition-colors';
const BTN_ICON = 'bg-transparent text-text-dim border border-transparent rounded-md w-7 h-7 cursor-pointer font-[inherit] flex items-center justify-center p-0 hover:text-accent-red hover:bg-[rgba(248,113,113,0.08)] hover:border-[rgba(248,113,113,0.35)] transition-colors';

/* Section heading (e.g. "Valid range", "Step changes") — strong label,
   not the pale text-dim of before. */
const SECTION_LABEL = 'text-[0.68rem] font-semibold text-accent-gold uppercase tracking-[0.1em]';

/* Input chrome — elevated, clearer focus ring. Placeholders are italic +
   lower opacity so they visibly read as suggestions, not values. */
const FIELD_WRAP = 'flex items-center bg-surface border border-border-strong rounded-md overflow-hidden focus-within:border-accent-blue focus-within:ring-1 focus-within:ring-accent-blue/30 transition-colors';
const FIELD_INPUT = 'bg-transparent text-text border-0 px-[0.65rem] py-[0.4rem] font-mono text-[0.85rem] tabular-nums tracking-[0.02em] focus:outline-none placeholder:text-text-dim placeholder:italic placeholder:opacity-60';

/* Per-row step input — inline in the table. */
const TBL_INPUT = 'bg-surface border border-border-strong text-text px-[0.55rem] py-[0.3rem] rounded-md font-[inherit] text-[0.82rem] font-mono tabular-nums focus:outline-none focus:border-accent-blue focus:ring-1 focus:ring-accent-blue/30 placeholder:text-text-dim placeholder:italic placeholder:opacity-60 transition-colors';
const TBL_NOTE_INPUT = 'bg-surface border border-border-strong text-text px-[0.55rem] py-[0.3rem] rounded-md font-[inherit] text-[0.82rem] focus:outline-none focus:border-accent-blue focus:ring-1 focus:ring-accent-blue/30 placeholder:text-text-dim placeholder:italic placeholder:opacity-60 transition-colors w-full';

const TH_CLS = 'text-left text-[0.66rem] text-text-muted font-semibold uppercase tracking-[0.08em] px-[0.5rem] pb-[0.35rem] pt-0 border-b border-border';
const TD_CLS = 'px-[0.5rem] py-[0.35rem] align-middle';
const TR_CLS = 'hover:bg-white/[0.02] transition-colors';

/* PiecewiseScheduleEditor — generalized month-range editor for a recurring line. */
export default function PiecewiseScheduleEditor({ code, label, lineDefault, scenario, setScenario, compact = false, fxInfo = null }) {
  const stored = scenario?.recurringOverrides?.[code]?.schedule;
  const effective = stored ?? lineDefault ?? { steps: [] };
  const isOverridden = !!stored;
  const { requestConfirmation, dialog: confirmDialog } = useAppConfirm();

  const write = (nextSchedule) => {
    const next = { ...(scenario.recurringOverrides ?? {}) };
    if (nextSchedule == null) {
      delete next[code];
    } else {
      next[code] = { schedule: nextSchedule };
    }
    setScenario({ ...scenario, recurringOverrides: next });
  };

  const setField = (key, value) => {
    write({ ...effective, [key]: value });
  };

  const setStep = (idx, patch) => {
    const steps = [...(effective.steps ?? [])];
    const current = { ...(steps[idx] ?? {}), ...patch };
    if (fxInfo && (patch.fxNative != null || patch.fxRate != null || (current.fxNative != null && current.amount == null))) {
      const native = current.fxNative ?? 0;
      const rate = current.fxRate ?? fxInfo.baseRate;
      current.amount = parseFloat((Number(native) * Number(rate)).toFixed(2));
      current.fx = { native: Number(native), baseRate: Number(rate) };
    }
    steps[idx] = current;
    write({ ...effective, steps });
  };

  const addStep = () => {
    const steps = [...(effective.steps ?? [])];
    const last = steps[steps.length - 1];
    const newStep = {
      fromYM: last?.fromYM ? bumpYM(last.fromYM, 1) : '2026-05',
      amount: last?.amount ?? (fxInfo ? 0 : 10000),
      note: '',
    };
    if (fxInfo) {
      newStep.fxNative = last?.fxNative ?? 0;
      newStep.fxRate   = last?.fxRate ?? fxInfo.baseRate;
      newStep.amount   = parseFloat((newStep.fxNative * newStep.fxRate).toFixed(2));
      newStep.fx       = { native: newStep.fxNative, baseRate: newStep.fxRate };
    }
    steps.push(newStep);
    write({ ...effective, steps });
  };

  const removeStep = (idx) => {
    const steps = (effective.steps ?? []).filter((_, i) => i !== idx);
    write({ ...effective, steps });
  };

  const reset = async () => {
    const ok = await requestConfirmation({
      title: 'Reset schedule?',
      message: `Reset "${label}" to the line's default schedule.`,
      confirmLabel: 'Reset schedule',
      tone: 'danger',
    });
    if (!ok) return;
    write(null);
  };

  /* Compact mode: plain panel embedded in a parent card (used in some inline
     editors). Full mode: standalone card with gradient bg and gold left
     accent that lights up when the user overrides the default schedule. */
  const wrapCls = compact
    ? 'bg-transparent border border-border rounded-md px-[0.7rem] py-[0.6rem] flex flex-col gap-[0.5rem]'
    : `bg-gradient-to-br from-surface-el to-surface rounded-lg border border-border-strong shadow-sm p-4 flex flex-col gap-3 transition-colors hover:border-[rgba(96,165,250,0.35)] ${isOverridden ? 'border-l-[3px] border-l-accent-gold' : ''}`;

  const lastStep = effective.steps?.[effective.steps.length - 1];
  const currentAmt = lastStep?.amount ?? 0;

  return (
    <>
    <div className={wrapCls}>
      {!compact && (
        <header className="flex items-center gap-2 flex-wrap">
          <strong className="text-[0.95rem] font-semibold text-text">{label}</strong>
          {currentAmt > 0 && (
            <span className={BADGE_AMT}>{fmtMoney(currentAmt, { decimals: 2 })}/mo</span>
          )}
          {fxInfo && <span className={BADGE_ZB}>{fxInfo.currency} · {fxInfo.baseRate}</span>}
          {isOverridden && <span className={BADGE_OVR}>Overridden</span>}
          {isOverridden && (
            <button type="button" className={`${BTN_RESET} ml-auto`} onClick={reset}>Reset to default</button>
          )}
        </header>
      )}

      {/* Valid-range block — grouped under one label, inline with arrow separator */}
      <div className="flex items-center gap-3 flex-wrap">
        <span className={SECTION_LABEL}>Valid range</span>
        <span className={FIELD_WRAP}>
          <input
            type="text"
            pattern="\d{4}-\d{2}"
            placeholder="YYYY-MM"
            value={effective.startYM ?? ''}
            onChange={(e) => setField('startYM', e.target.value || null)}
            aria-label="Start year-month"
            className={FIELD_INPUT}
            style={{ width: 104 }}
          />
        </span>
        <span className="text-text-dim text-[0.85rem]">→</span>
        <span className={FIELD_WRAP}>
          <input
            type="text"
            pattern="\d{4}-\d{2}"
            placeholder="open-ended"
            value={effective.stopYM ?? ''}
            onChange={(e) => setField('stopYM', e.target.value || null)}
            aria-label="Stop year-month"
            className={FIELD_INPUT}
            style={{ width: 104 }}
          />
        </span>
        <span className="text-[0.7rem] text-text-dim italic">Leave stop empty for indefinite</span>
      </div>

      {/* Step changes block — clearly labelled, table with alternating hover */}
      <div className="flex flex-col gap-1 pt-1">
        <span className={SECTION_LABEL}>Step changes</span>
        <div className="overflow-x-auto">
        <table className="w-full border-collapse text-[0.82rem]">
        <thead>
          <tr>
            <th className={TH_CLS}>From YM</th>
            {fxInfo && <th className={TH_CLS}>{fxInfo.currency}</th>}
            {fxInfo && <th className={TH_CLS}>Rate</th>}
            <th className={TH_CLS}>{fxInfo ? 'Reporting (auto)' : 'Amount'}</th>
            <th className={`${TH_CLS} w-full`}>Note</th>
            <th className={TH_CLS}></th>
          </tr>
        </thead>
        <tbody>
          {(effective.steps ?? []).length === 0 && (
            <tr>
              <td colSpan={fxInfo ? 6 : 4} className="px-[0.5rem] py-3 text-center text-[0.82rem] text-text-dim italic">
                No step changes. Click <span className="text-accent-blue not-italic font-semibold">+ Add step</span> to define one.
              </td>
            </tr>
          )}
          {(effective.steps ?? []).map((s, i) => (
            <tr key={i} className={TR_CLS}>
              <td className={TD_CLS}>
                <input
                  type="text"
                  pattern="\d{4}-\d{2}"
                  placeholder="YYYY-MM"
                  value={s.fromYM ?? ''}
                  onChange={(e) => setStep(i, { fromYM: e.target.value || null })}
                  className={`${TBL_INPUT} font-mono tracking-[0.02em]`}
                  style={{ width: 100 }}
                />
              </td>
              {fxInfo && (
                <td className={TD_CLS}>
                  <NumericField
                    className={`${TBL_INPUT} text-right`}
                    style={{ width: 80 }}
                    value={s.fxNative ?? (s.amount && fxInfo ? Number((s.amount / (s.fxRate ?? fxInfo.baseRate)).toFixed(2)) : 0)}
                    onChange={(v) => setStep(i, { fxNative: v })}
                    decimals={2}
                  />
                </td>
              )}
              {fxInfo && (
                <td className={TD_CLS}>
                  <NumericField
                    className={`${TBL_INPUT} text-right`}
                    style={{ width: 70 }}
                    value={s.fxRate ?? fxInfo.baseRate}
                    onChange={(v) => setStep(i, { fxRate: v })}
                    decimals={2}
                  />
                </td>
              )}
              <td className={TD_CLS}>
                {fxInfo
                  ? <span className="inline-block px-[6px] py-[2px] bg-surface-raised border border-dashed border-border rounded-[3px] text-accent-gold font-mono text-[0.78rem] tabular-nums">{fmtMoney(s.amount ?? 0, { decimals: 2 })}</span>
                  : (
                    <NumericField
                      className={`${TBL_INPUT} text-right`}
                      style={{ width: 110 }}
                      value={s.amount ?? 0}
                      onChange={(v) => setStep(i, { amount: v })}
                      decimals={2}
                    />
                  )
                }
              </td>
              <td className={TD_CLS}>
                <input
                  type="text"
                  placeholder="what changed at this step…"
                  value={s.note ?? ''}
                  onChange={(e) => setStep(i, { note: e.target.value })}
                  className={TBL_NOTE_INPUT}
                />
              </td>
              <td className={TD_CLS}>
                <button type="button" className={BTN_ICON} onClick={() => removeStep(i)} aria-label="Remove step" title="Remove this step">✕</button>
              </td>
            </tr>
          ))}
        </tbody>
        </table>
        </div>
        <div className="flex gap-2 flex-wrap pt-1">
          <button type="button" className={BTN_GHOST_SM} onClick={addStep}>+ Add step</button>
          {compact && isOverridden && (
            <button type="button" className={BTN_RESET} onClick={reset}>Reset to default</button>
          )}
        </div>
      </div>
    </div>
    {confirmDialog}
    </>
  );
}

function bumpYM(ym, months) {
  const [y, m] = ym.split('-').map(Number);
  const total = y * 12 + (m - 1) + months;
  return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, '0')}`;
}
